import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Truck, Save } from "lucide-react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import {
  DAY_LABELS,
  type DeliveryConfigDay,
  fetchDeliveryConfig,
  updateDeliveryConfig,
} from "@/lib/deliveryApi";

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];

export default function AdminDeliveryConfig() {
  const canEdit = hasPermission(useApp((s) => s.account), "ENTREGAS.CONFIG.EDITAR");
  const [config, setConfig] = useState<DeliveryConfigDay[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      setConfig(await fetchDeliveryConfig());
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const getDay = (dow: number): DeliveryConfigDay =>
    config.find((d) => d.dayOfWeek === dow) ?? { dayOfWeek: dow, cutoffHour: 18, active: false };

  const setDayField = <K extends keyof DeliveryConfigDay>(
    dow: number,
    field: K,
    value: DeliveryConfigDay[K],
  ) => {
    setConfig((prev) => {
      const existing = prev.find((d) => d.dayOfWeek === dow);
      if (existing) {
        return prev.map((d) => (d.dayOfWeek === dow ? { ...d, [field]: value } : d));
      }
      return [...prev, { dayOfWeek: dow, cutoffHour: 18, active: false, [field]: value }];
    });
  };

  const handleSave = async () => {
    // Ensure all 7 days are present
    const full = ALL_DAYS.map((dow) => getDay(dow));
    setSaving(true);
    try {
      const updated = await updateDeliveryConfig(full);
      setConfig(updated);
      toast.success("Configuração de entregas salva");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível salvar"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-3 py-1 text-xs font-medium">
                <Truck className="h-3 w-3" /> Janelas de entrega
              </div>
              <h1 className="font-display text-3xl font-bold tracking-tight">Configurar Entregas</h1>
              <p className="mt-1 text-sm text-primary-foreground/80">
                Defina os dias e horários de corte para pedidos de entrega.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="container py-8 max-w-2xl">
        {loadState === "loading" && (
          <div className="surface-card p-12 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
            Carregando...
          </div>
        )}
        {loadState === "error" && (
          <div className="surface-card p-12 text-center text-sm text-destructive">
            Não foi possível carregar a configuração.
          </div>
        )}
        {loadState === "ready" && (
          <div className="surface-card divide-y divide-border overflow-hidden">
            {ALL_DAYS.map((dow) => {
              const day = getDay(dow);
              return (
                <div key={dow} className="flex items-center gap-4 px-5 py-4">
                  <input
                    type="checkbox"
                    id={`day-${dow}`}
                    checked={day.active}
                    disabled={!canEdit}
                    onChange={(e) => setDayField(dow, "active", e.target.checked)}
                    className="h-4 w-4 rounded border-border accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  <Label
                    htmlFor={`day-${dow}`}
                    className="flex-1 text-sm font-medium cursor-pointer select-none"
                  >
                    {DAY_LABELS[dow]}
                  </Label>
                  {day.active && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">
                        Corte às
                      </Label>
                      <input
                        type="number"
                        min={0}
                        max={23}
                        value={day.cutoffHour}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setDayField(dow, "cutoffHour", Number(e.target.value))
                        }
                        className="h-9 w-16 rounded-md border border-input bg-background px-3 text-sm tabular-nums disabled:cursor-not-allowed disabled:opacity-60"
                      />
                      <span className="text-sm text-muted-foreground">h</span>
                    </div>
                  )}
                </div>
              );
            })}

            {canEdit && (
              <div className="px-5 py-4 flex justify-end">
                <Button onClick={() => void handleSave()} disabled={saving}>
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Salvar configuração
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
